import React, { useState, useMemo, useRef, useEffect } from 'react';

/* =====================================================================
   VALIDASI.JS
   Khusus validasi transaksi supplier (Reguler, Paket Telpon, Paket Data).
   Fitur Broadcast & Template ada di file terpisah: Broadcast.js
   ===================================================================== */

// ---------- KONFIGURASI TIPE VALIDASI ----------
const VALIDATION_TYPES = [
    {
        id: 'reguler',
        label: 'Reguler',
        kegunaan: 'Khusus Pulsa Byu & Telkomsel',
        dibaca: 'Nomor/Tujuan, SN/Ref ID, Produk, Status, Waktu',
        format: '1 baris: waktu, nominal, tujuan, SN, status',
        contoh: '2025-01-15 10:30:22 10 6281234567890 123456789012345 Success'
    },
    {
        id: 'telpon',
        label: 'Paket Telpon',
        kegunaan: 'Khusus Paket Telpon Pas',
        dibaca: 'Nomor/Tujuan, SN/Ref ID, Waktu',
        format: '1 kalimat konfirmasi isi paket berhasil',
        contoh: 'Isi paket untuk no. 6281234567890 telah berhasil dengan SN : 123456789012345 pada 15/01/25 10:30:22'
    },
    {
        id: 'data',
        label: 'Paket Data',
        kegunaan: 'Khusus Telkomsel Data',
        dibaca: 'Nomor/Tujuan, SN/Ref ID, Produk, Waktu',
        format: '6 baris: SN, waktu, waktu, tujuan, produk, success',
        contoh: '123456789012345\n15/01/25 10:30:22\n15/01/25 10:30:22\n6281234567890\nInternet 5GB 30Hari\nsuccess'
    }
];

const TYPE_LABEL = VALIDATION_TYPES.reduce((acc, t) => ({ ...acc, [t.id]: t.label }), {});
const PAGE_SIZE = 10;

// ---------- HELPER WAKTU ----------
const pad = (n) => String(n).padStart(2, '0');

const formatLocal = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

const formatShort = (d) =>
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

// Mendukung "YYYY-MM-DD HH:mm:ss" dan "DD/MM HH:mm:ss" (tahun mengikuti tahun berjalan).
// Jika tidak ada waktu di dalam blok, dipakai waktu sekarang. Semua hasil ditambah 10 detik.
const parseBlockDate = (block) => {
    const match = block.match(
        /(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2}):(\d{2})|(\d{2})\/(\d{2})\s(\d{2}):(\d{2}):(\d{2})/
    );
    let date = new Date();
    if (match) {
        const parsed = match[1]
            ? new Date(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +match[6])
            : new Date(new Date().getFullYear(), +match[8] - 1, +match[7], +match[9], +match[10], +match[11]);
        if (!isNaN(parsed.getTime())) date = parsed;
    }
    date.setSeconds(date.getSeconds() + 10);
    return date;
};

// ---------- LOGIKA UTAMA VALIDATOR ----------
const extractData = (rawText, type) => {
    const blocks = rawText.split(/\n\s*\n/).filter((b) => b.trim() !== '');
    const records = [];

    blocks.forEach((block) => {
        const getVal = (key) => {
            const pattern = new RegExp(`${key}\\s*[:=]\\s*([^\\n,]+)`, 'i');
            const match = block.match(pattern);
            return match ? match[1].trim() : null;
        };

        const tujuanRaw = getVal('Nomor') || getVal('Tujuan') || block.match(/08\d{8,11}|628\d{8,11}/)?.[0];
        if (!tujuanRaw) return;

        const tujuan = tujuanRaw.startsWith('0') ? '62' + tujuanRaw.slice(1) : tujuanRaw;
        const sn = getVal('SN') || getVal('Ref ID') || block.match(/\d{15,22}/)?.[0] || '0';
        const lower = block.toLowerCase();
        const isSuccess = lower.includes('sukses') || lower.includes('success') || lower.includes('berhasil');

        const dateObj = parseBlockDate(block);
        const fullTime = formatLocal(dateObj);
        const shortTime = formatShort(dateObj);

        let output = '';
        let produk = '-';

        if (type === 'reguler') {
            const rawProduct = getVal('Produk');
            const p = rawProduct || block.match(/(?:SP|TSEL|DATA|PLN)\s?(\d+)/i)?.[1] || '';
            const nominal = p.match(/\d+/)?.[0] || '0';
            produk = rawProduct || (nominal !== '0' ? nominal : '-');
            output = `${fullTime} ${nominal} ${tujuan} ${sn} ${isSuccess ? 'Success' : 'Failed'}`;
        } else if (type === 'telpon') {
            produk = getVal('Produk') || '-';
            output = `Isi paket untuk no. ${tujuan} telah berhasil dengan SN : ${sn} pada ${shortTime}`;
        } else if (type === 'data') {
            const [tglOnly, jamOnly] = shortTime.split(' ');
            const rawProduct = getVal('Produk') || '';
            let processedProduct = 'Internet';

            if (rawProduct) {
                const detailMatch = rawProduct.match(/\d+\s*(?:GB|MB|Hari|Hr|Bulan)/gi);
                if (detailMatch) processedProduct = `Internet ${detailMatch.join(' ')}`;
            }

            produk = processedProduct;
            output = `${sn}\n${tglOnly} ${jamOnly}\n${tglOnly} ${jamOnly}\n${tujuan}\n${processedProduct}\nsuccess`;
        }

        records.push({
            type,
            waktu: fullTime,
            tujuan,
            sn,
            produk,
            status: isSuccess ? 'success' : 'failed',
            output
        });
    });

    return records;
};

// ---------- KOMPONEN KECIL ----------
const StatusBadge = ({ status }) => (
    <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${
            status === 'success'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
        }`}
    >
        <span className={`w-1.5 h-1.5 rounded-full ${status === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} />
        {status === 'success' ? 'Sukses' : 'Gagal'}
    </span>
);

const thClass = 'px-4 py-3 text-left text-[11px] font-semibold text-slate-500 whitespace-nowrap border-b border-slate-200';
const tdClass = 'px-4 py-3 align-top text-xs text-slate-700 border-b border-slate-100';
const primaryBg = { backgroundColor: 'var(--primary-color, #4f46e5)' };

// =====================================================================
const Validator = () => {
    const [activeType, setActiveType] = useState('reguler');
    const [inputRaw, setInputRaw] = useState('');
    const [results, setResults] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');

    const [typeFilter, setTypeFilter] = useState('semua');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const [toast, setToast] = useState({ show: false, msg: '' });
    const toastTimer = useRef(null);
    const idCounter = useRef(1);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const showToast = (msg) => {
        clearTimeout(toastTimer.current);
        setToast({ show: true, msg });
        toastTimer.current = setTimeout(() => setToast({ show: false, msg: '' }), 2000);
    };

    const copyText = async (text, successMsg = 'Berhasil disalin') => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        showToast(successMsg);
    };

    // ---------- PROSES ----------
    const blockCount = useMemo(
        () => inputRaw.split(/\n\s*\n/).filter((b) => b.trim() !== '').length,
        [inputRaw]
    );

    const handleProcess = () => {
        if (!inputRaw.trim()) {
            setErrorMsg('Area input masih kosong. Tempelkan log transaksi dari supplier terlebih dahulu.');
            return;
        }
        const processed = extractData(inputRaw, activeType).map((rec) => ({ ...rec, id: idCounter.current++ }));
        if (processed.length === 0) {
            setErrorMsg('Format tidak dikenali. Pastikan setiap transaksi memuat Nomor/Tujuan (08xx atau 628xx) dan dipisahkan satu baris kosong.');
            return;
        }
        setErrorMsg('');
        setResults((prev) => [...processed, ...prev]);
        setInputRaw('');
        setPage(1);
        showToast(`${processed.length} transaksi berhasil diproses`);
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleProcess();
    };

    const handleDeleteResult = (id) => setResults((prev) => prev.filter((r) => r.id !== id));

    const handleClearAll = () => {
        if (window.confirm('Hapus semua hasil validasi pada sesi ini?')) {
            setResults([]);
            setPage(1);
        }
    };

    // ---------- FILTER & PAGINATION ----------
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return results.filter((r) => {
            if (typeFilter !== 'semua' && r.type !== typeFilter) return false;
            if (!q) return true;
            return (
                r.tujuan.toLowerCase().includes(q) ||
                r.sn.toLowerCase().includes(q) ||
                r.output.toLowerCase().includes(q)
            );
        });
    }, [results, typeFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    const pageRows = filtered.slice(startIdx, startIdx + PAGE_SIZE);

    const stats = useMemo(
        () => ({
            total: results.length,
            sukses: results.filter((r) => r.status === 'success').length,
            gagal: results.filter((r) => r.status === 'failed').length
        }),
        [results]
    );

    const handleCopyAll = () => {
        if (filtered.length === 0) return;
        const hasMultiline = filtered.some((r) => r.output.includes('\n'));
        copyText(
            filtered.map((r) => r.output).join(hasMultiline ? '\n\n' : '\n'),
            `${filtered.length} hasil disalin`
        );
    };

    const activeConfig = VALIDATION_TYPES.find((t) => t.id === activeType);

    // =================================================================
    return (
        <div className="max-w-7xl mx-auto space-y-6 pb-12 relative">

            {/* Toast */}
            {toast.show && (
                <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[3000] w-[90%] md:w-auto">
                    <div className="bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-white/10">
                        <span className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-[10px]">✓</span>
                        <span className="text-xs font-semibold">{toast.msg}</span>
                    </div>
                </div>
            )}

            {/* HEADER */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                <div>
                    <h2 className="text-lg md:text-xl font-extrabold text-slate-800 tracking-tight">Validasi Transaksi</h2>
                    <p className="text-xs text-slate-400 mt-1 max-w-xl leading-relaxed">
                        Ubah log transaksi supplier menjadi format laporan yang siap disalin. Pilih tipe validasi, tempelkan log, lalu proses.
                    </p>
                </div>
                <div className="grid grid-cols-3 gap-3 shrink-0">
                    {[
                        { label: 'Total', value: stats.total, cls: 'text-slate-800' },
                        { label: 'Sukses', value: stats.sukses, cls: 'text-emerald-600' },
                        { label: 'Gagal', value: stats.gagal, cls: 'text-red-600' }
                    ].map((s) => (
                        <div key={s.label} className="min-w-[88px] px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                            <p className="text-[11px] font-medium text-slate-400">{s.label}</p>
                            <p className={`text-lg font-extrabold font-mono leading-tight ${s.cls}`}>{s.value}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* TABEL TIPE VALIDASI */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                        <h3 className="text-sm font-bold text-slate-800">Tipe Validasi</h3>
                        <p className="text-[11px] text-slate-400 mt-0.5">Klik baris untuk memilih format yang sesuai dengan data supplier.</p>
                    </div>
                    <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-slate-500">
                        Mode aktif
                        <span style={primaryBg} className="text-white px-2.5 py-1 rounded-md text-[11px] font-bold">
                            {activeConfig.label}
                        </span>
                    </span>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[920px] border-collapse" role="radiogroup" aria-label="Tipe validasi">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className={`${thClass} w-14 text-center`}>Pilih</th>
                                <th className={thClass}>Tipe</th>
                                <th className={thClass}>Kegunaan</th>
                                <th className={thClass}>Data yang dibaca</th>
                                <th className={thClass}>Format hasil</th>
                                <th className={thClass}>Contoh hasil</th>
                            </tr>
                        </thead>
                        <tbody>
                            {VALIDATION_TYPES.map((t) => {
                                const isActive = activeType === t.id;
                                return (
                                    <tr
                                        key={t.id}
                                        onClick={() => setActiveType(t.id)}
                                        className={`cursor-pointer transition-colors ${isActive ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}
                                        style={isActive ? { boxShadow: 'inset 3px 0 0 var(--primary-color, #4f46e5)' } : undefined}
                                    >
                                        <td className={`${tdClass} text-center`}>
                                            <input
                                                type="radio"
                                                name="tipeValidasi"
                                                checked={isActive}
                                                onChange={() => setActiveType(t.id)}
                                                aria-label={t.label}
                                                className="w-4 h-4 cursor-pointer"
                                                style={{ accentColor: 'var(--primary-color, #4f46e5)' }}
                                            />
                                        </td>
                                        <td className={`${tdClass} font-bold text-slate-800 whitespace-nowrap`}>{t.label}</td>
                                        <td className={`${tdClass} text-slate-500`}>{t.kegunaan}</td>
                                        <td className={`${tdClass} text-slate-500`}>{t.dibaca}</td>
                                        <td className={`${tdClass} text-slate-500`}>{t.format}</td>
                                        <td className={tdClass}>
                                            <pre className="font-mono text-[11px] text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 whitespace-pre-wrap break-all max-w-sm">
                                                {t.contoh}
                                            </pre>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* INPUT */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                    <div>
                        <h3 className="text-sm font-bold text-slate-800">Input Laporan Supplier</h3>
                        <p className="text-[11px] text-slate-400 mt-0.5">Pisahkan setiap transaksi dengan satu baris kosong. Tekan Ctrl + Enter untuk memproses.</p>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md self-start sm:self-auto">
                        {blockCount} blok transaksi terdeteksi
                    </span>
                </div>

                <textarea
                    value={inputRaw}
                    onChange={(e) => { setInputRaw(e.target.value); if (errorMsg) setErrorMsg(''); }}
                    onKeyDown={handleKeyDown}
                    spellCheck={false}
                    placeholder="Tempelkan log laporan supplier di sini..."
                    className="w-full h-48 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-700 outline-none focus:bg-white focus:border-slate-400 focus:ring-2 focus:ring-slate-200 transition-all placeholder:text-slate-300 resize-y"
                />

                {errorMsg && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[11px] font-semibold">
                        {errorMsg}
                    </div>
                )}

                <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => { setInputRaw(''); setErrorMsg(''); }}
                        disabled={!inputRaw}
                        className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Kosongkan input
                    </button>
                    <button
                        type="button"
                        onClick={handleProcess}
                        style={primaryBg}
                        className="px-6 py-2.5 rounded-xl text-white text-xs font-bold shadow-md hover:opacity-90 active:scale-[0.98] transition-all"
                    >
                        Proses sebagai {activeConfig.label}
                    </button>
                </div>
            </div>

            {/* TABEL HASIL */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                    <div>
                        <h3 className="text-sm font-bold text-slate-800">Hasil Validasi</h3>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                            Menampilkan {filtered.length} dari {results.length} transaksi sesi ini.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <select
                            value={typeFilter}
                            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none focus:border-slate-400"
                            aria-label="Filter tipe"
                        >
                            <option value="semua">Semua tipe</option>
                            {VALIDATION_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Cari nomor atau SN..."
                            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-slate-400 w-full sm:w-52"
                        />
                        <button
                            type="button"
                            onClick={handleCopyAll}
                            disabled={filtered.length === 0}
                            style={primaryBg}
                            className="px-4 py-2 rounded-xl text-white text-xs font-bold hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            Salin semua
                        </button>
                        <button
                            type="button"
                            onClick={handleClearAll}
                            disabled={results.length === 0}
                            className="px-4 py-2 rounded-xl border border-red-200 bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            Hapus semua
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1040px] border-collapse">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className={`${thClass} w-12 text-center`}>No</th>
                                <th className={thClass}>Waktu</th>
                                <th className={thClass}>Tipe</th>
                                <th className={thClass}>Tujuan</th>
                                <th className={thClass}>SN / Ref ID</th>
                                <th className={thClass}>Produk</th>
                                <th className={thClass}>Status</th>
                                <th className={thClass}>Hasil validasi</th>
                                <th className={`${thClass} text-right`}>Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map((r, i) => (
                                <tr key={r.id} className="hover:bg-slate-50/60 transition-colors">
                                    <td className={`${tdClass} text-center text-slate-400 font-mono`}>{startIdx + i + 1}</td>
                                    <td className={`${tdClass} font-mono whitespace-nowrap`}>{r.waktu}</td>
                                    <td className={`${tdClass} whitespace-nowrap`}>
                                        <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-semibold text-slate-600">
                                            {TYPE_LABEL[r.type]}
                                        </span>
                                    </td>
                                    <td className={`${tdClass} font-mono whitespace-nowrap`}>{r.tujuan}</td>
                                    <td className={`${tdClass} font-mono break-all max-w-[180px]`}>{r.sn}</td>
                                    <td className={`${tdClass} max-w-[160px]`}>{r.produk}</td>
                                    <td className={tdClass}><StatusBadge status={r.status} /></td>
                                    <td className={tdClass}>
                                        <pre className="font-mono text-[11px] text-slate-700 whitespace-pre-wrap break-all max-w-xs leading-relaxed">
                                            {r.output}
                                        </pre>
                                    </td>
                                    <td className={`${tdClass} text-right whitespace-nowrap`}>
                                        <div className="inline-flex gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => copyText(r.output)}
                                                className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[11px] font-bold hover:bg-slate-700 transition-colors"
                                            >
                                                Salin
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteResult(r.id)}
                                                aria-label="Hapus baris"
                                                className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 text-[11px] font-bold hover:bg-red-100 transition-colors"
                                            >
                                                Hapus
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {pageRows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-4 py-14 text-center">
                                        <p className="text-sm font-bold text-slate-500">
                                            {results.length === 0 ? 'Belum ada hasil validasi' : 'Tidak ada data yang cocok dengan filter'}
                                        </p>
                                        <p className="text-[11px] text-slate-400 mt-1">
                                            {results.length === 0
                                                ? 'Tempelkan log supplier pada area input lalu klik Proses.'
                                                : 'Ubah tipe atau kata kunci pencarian.'}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {filtered.length > PAGE_SIZE && (
                    <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between text-xs text-slate-500">
                        <span>
                            Baris {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} dari {filtered.length}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Sebelumnya
                            </button>
                            <span className="font-mono font-semibold text-slate-600">{currentPage} / {totalPages}</span>
                            <button
                                type="button"
                                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Berikutnya
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Validator;